import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const RequestManagementPage = ({ onNavigate, userInfo }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  // User privileges state
  const [userPrivileges, setUserPrivileges] = useState([]);
  const [privilegesLoading, setPrivilegesLoading] = useState(true);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchDashboardData();
      fetchUserPrivileges();
      fetchRequests();
      fetchStats();
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo?.userId) {
      const timer = setTimeout(() => {
        fetchRequests();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedCategory, selectedStatus, selectedPriority, searchQuery]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/dashboard/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  // Fetch user privileges
  const fetchUserPrivileges = async () => {
    try {
      setPrivilegesLoading(true);
      const response = await fetch(`${API_URL}/admin/user-privileges/${userInfo.userId}`);
      const data = await response.json();
      
      console.log('Privileges response:', data);
      
      if (data.success && data.privileges) {
        setUserPrivileges(data.privileges);
      } else {
        console.error('Failed to load privileges:', data.message);
        setUserPrivileges([]);
      }
    } catch (err) {
      console.error('Error fetching privileges:', err);
      setUserPrivileges([]);
    } finally {
      setPrivilegesLoading(false);
    }
  };

  // Group privileges by category
  const getPrivilegesByCategory = (category) => {
    return userPrivileges.filter(privilege => privilege.page_category === category);
  };


  // Check if user has access to this page
  useEffect(() => {
    if (!privilegesLoading && userPrivileges.length > 0) {
      const hasAccess = userPrivileges.some(
        privilege => privilege.page_route === 'hostel-admin-request'
      );
      
      if (!hasAccess) {
        alert('⚠️ Access Denied: You do not have permission to view this page.');
        if (onNavigate) {
          onNavigate('hostel-admin-dashboard');
        }
      }
    }
  }, [privilegesLoading, userPrivileges, onNavigate]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (selectedCategory !== 'all') queryParams.append('issueType', selectedCategory);
      if (selectedStatus !== 'all') queryParams.append('status', selectedStatus);
      if (selectedPriority !== 'all') queryParams.append('priority', selectedPriority);
      if (searchQuery) queryParams.append('search', searchQuery);

      const response = await fetch(
        `${API_URL}/admin/maintenance-requests/${userInfo.userId}?${queryParams}`
      );
      const data = await response.json();

      if (data.success) {
        setRequests(data.requests || []);
        if (data.stats) {
          setStats(data.stats);
        }
      } else {
        setError(data.message || 'Failed to load requests');
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_URL}/admin/maintenance-requests-stats/${userInfo.userId}`
      );
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const updateRequestStatus = async (requestId, newStatus, assignedTo = null) => {
    setUpdateLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/admin/maintenance-request/${userInfo.userId}/${requestId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: newStatus,
            assignedTo: assignedTo
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        setSelectedRequest(null);
        await fetchRequests();
        await fetchStats();
        alert('Request updated successfully!');
      } else {
        alert(data.message || 'Failed to update request');
      }
    } catch (err) {
      console.error('Error updating request:', err);
      alert('Failed to update request');
    } finally {
      setUpdateLoading(false);
    }
  };

  // Sample notifications
  const notifications = [
    {
      id: 1,
      title: 'New Maintenance Request',
      message: 'New electrical issue reported in Room 204',
      time: '5 minutes ago',
      icon: '🔧',
      read: false
    },
    {
      id: 2,
      title: 'Request Completed',
      message: 'AC repair completed for Room 201',
      time: '30 minutes ago',
      icon: '✅',
      read: false
    }
  ];

  const MenuItem = ({ icon, label, active = false, route }) => (
    <div 
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setSidebarOpen(false);
      }}
    >
      <span className="menu-icon">{icon}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const handleLogout = () => {
    sessionStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#f59e0b';
      case 'in-progress': return '#3b82f6';
      case 'completed': return '#16a34a';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return '#f97316';
      case 'medium': return '#eab308';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category?.toLowerCase()) {
      case 'electrical': return '⚡';
      case 'plumbing': return '💧';
      case 'cleaning': return '🧹';
      case 'furniture': return '🪑';
      case 'ac': return '❄️';
      case 'internet': return '🌐';
      case 'door': return '🚪';
      default: return '📋';
    }
  };

  if ((loading && requests.length === 0) || privilegesLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Inter, sans-serif',
        fontSize: '18px',
        color: '#2b6777'
      }}>
        Loading requests...
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
        }

        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .dashboard-container {
          display: flex;
          height: 100vh;
          background: #ffffff;
          overflow: hidden;
        }

        .sidebar {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: 280px;
          background: #f5f7f6;
          transform: translateX(-100%);
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 100;
          display: flex;
          flex-direction: column;
          box-shadow: 8px 0 40px rgba(43,103,119,0.15);
        }

        .sidebar.open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 24px 20px;
          background: linear-gradient(135deg, rgba(199,229,225,0.3) 0%, rgba(242,248,247,0.5) 100%);
          border-bottom: 1px solid rgba(82,171,152,0.15);
        }

        .sidebar-top {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 16px;
        }

        .close-btn {
          background: rgba(43,103,119,0.1);
          border: none;
          cursor: pointer;
          font-size: 18px;
          color: #2b6777;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .close-btn:hover {
          background: rgba(43,103,119,0.2);
          transform: rotate(90deg);
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
          z-index: 1;
        }

        .user-avatar-sidebar {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 800;
          font-size: 20px;
          box-shadow: 0 4px 12px rgba(43,103,119,0.2);
          flex-shrink: 0;
        }

        .user-details-sidebar {
          flex: 1;
        }

        .user-name-sidebar {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .user-role-sidebar {
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        .sidebar-nav {
          flex: 1;
          overflow-y: auto;
          padding: 24px 16px;
          background: #f5f7f6;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .nav-section {
          margin-bottom: 28px;
        }

        .nav-label {
          font-size: 11px;
          font-weight: 700;
          color: rgba(43,103,119,0.45);
          text-transform: uppercase;
          letter-spacing: 1.2px;
          margin-bottom: 12px;
          padding: 0 12px;
          padding-left: 18px;
        }

        .menu-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          margin-bottom: 10px;
          cursor: pointer;
          color: #2b6777;
          font-size: 15px;
          font-weight: 600;
          border-radius: 14px;
          transition: all 0.3s;
          overflow: hidden;
        }

        .menu-glow {
          display: none;}

        .menu-item:hover {
          color: #52ab98;
          background: rgba(82,171,152,0.1);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .menu-item:hover .menu-glow {
          display: none;}

        .menu-item-active {
          background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
          color: #2b6777;
          box-shadow: 0 6px 20px rgba(82,171,152,0.2);
          font-weight: 700;
        }

        .menu-item-active .menu-glow {
          display: none;}

        .menu-icon {
          font-size: 22px;
          position: relative;
          z-index: 1;
        }

        .menu-label {
          position: relative;
          z-index: 1;
        }

        .logout-section {
          padding: 16px;
          background: #f5f7f6;
        }

        .logout-btn {
          width: 100%;
          padding: 16px 20px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        .logout-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 35px rgba(43,103,119,0.4);
        }

        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.4);
          backdrop-filter: blur(6px);
          z-index: 90;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }

        .overlay.show {
          opacity: 1;
          visibility: visible;
        }


        .no-privileges-message {
          padding: 40px 20px;
          text-align: center;
          background: rgba(255,249,196,0.3);
          border-radius: 12px;
          margin: 20px;
        }

        .no-privileges-icon {
          font-size: 48px;
          margin-bottom: 12px;
        }

        .no-privileges-text {
          font-size: 16px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 6px;
        }

        .no-privileges-subtext {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
        }

        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          width: 100%;
        }

        .top-header {
          background: #ffffff;
          padding: 52px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid rgba(82,171,152,0.12);
          position: relative;
          box-shadow: 0 2px 8px rgba(43,103,119,0.06);
        }

        .top-header::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 24px;
        }

        .menu-toggle {
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border: 2px solid rgba(82,171,152,0.25);
          cursor: pointer;
          font-size: 26px;
          color: #2b6777;
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          box-shadow: 0 4px 20px rgba(43,103,119,0.15);
        }

        .menu-toggle:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 30px rgba(43,103,119,0.25);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .header-greeting {
          font-size: 18px;
          color: #52ab98;
          font-weight: 600;
        }

        .header-name {
          font-size: 28px;
          font-weight: 900;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .notification-btn {
          position: relative;
          background: rgba(82,171,152,0.1);
          border: 2px solid rgba(82,171,152,0.2);
          cursor: pointer;
          font-size: 24px;
          color: #2b6777;
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .notification-btn:hover,
        .notification-btn.active {
          background: rgba(82,171,152,0.2);
          border-color: #52ab98;
          box-shadow: 0 6px 24px rgba(82,171,152,0.3);
          transform: scale(1.05);
        }

        .notification-badge {
          position: absolute;
          top: 6px;
          right: 6px;
          min-width: 22px;
          height: 22px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 11px;
          font-size: 11px;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 6px;
          color: white;
          border: 2px solid white;
          box-shadow: 0 3px 12px rgba(43,103,119,0.4);
          animation: pulse 2s infinite;
        }

        .user-avatar-header {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 20px;
          color: white;
          cursor: pointer;
          border: 3px solid rgba(82,171,152,0.3);
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        .user-avatar-header:hover {
          transform: scale(1.1);
          box-shadow: 0 8px 32px rgba(43,103,119,0.5);
        }

        .notifications-panel {
          position: fixed;
          top: 100px;
          right: 32px;
          width: 440px;
          max-width: calc(100vw - 64px);
          max-height: 640px;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(40px);
          border-radius: 24px;
          box-shadow: 0 24px 70px rgba(43,103,119,0.25);
          z-index: 1000;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-20px);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: 2px solid rgba(82,171,152,0.2);
          overflow: hidden;
        }

        .notifications-panel.open {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .notifications-header {
          padding: 28px 28px 24px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          z-index: 1;
        }

        .notifications-title {
          font-size: 20px;
          font-weight: 900;
          color: #2b6777;
        }

        .close-notification-btn {
          background: rgba(82,171,152,0.1);
          border: 1.5px solid rgba(82,171,152,0.25);
          color: #2b6777;
          cursor: pointer;
          width: 32px;
          height: 32px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          font-size: 16px;
        }

        .close-notification-btn:hover {
          background: rgba(82,171,152,0.2);
          border-color: #52ab98;
        }

        .notifications-list {
          max-height: 480px;
          overflow-y: auto;
          position: relative;
          z-index: 1;
        }

        .notification-item {
          padding: 24px 28px;
          border-bottom: 1px solid rgba(82,171,152,0.1);
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          gap: 18px;
          align-items: flex-start;
          position: relative;
        }

        .notification-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: transparent;
          transition: all 0.3s;
        }

        .notification-item:hover {
          background: rgba(82,171,152,0.08);
        }

        .notification-item:hover::before {
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
        }

        .notification-item.unread {
          background: rgba(82,171,152,0.05);
        }

        .notification-icon-wrapper {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          flex-shrink: 0;
          background: rgba(82,171,152,0.1);
          border: 1.5px solid rgba(82,171,152,0.2);
        }

        .notification-content {
          flex: 1;
        }

        .notification-header-item {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }

        .notification-title {
          font-size: 15px;
          font-weight: 800;
          color: #2b6777;
          flex: 1;
        }

        .notification-unread-dot {
          width: 10px;
          height: 10px;
          background: #52ab98;
          border-radius: 50%;
          box-shadow: 0 0 12px #52ab98;
        }

        .notification-message {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          margin-bottom: 8px;
          line-height: 1.6;
        }

        .notification-time {
          font-size: 12px;
          color: rgba(43,103,119,0.5);
          font-weight: 700;
        }

        .notification-overlay {
          position: fixed;
          inset: 0;
          z-index: 999;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s;
        }

        .notification-overlay.show {
          opacity: 1;
          visibility: visible;
        }

        .content-area {
          flex: 1;
          overflow-y: auto;
          background: #ffffff;
          padding: 32px;
        }

        .content-area::-webkit-scrollbar {
          width: 8px;
        }

        .content-area::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .content-area::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 4px;
        }

        .page-header {
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          padding: 40px 32px;
          margin-bottom: 32px;
          border-radius: 24px;
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(82,171,152,0.15);
        }

        .page-header::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
        }

        .page-title {
          font-size: 36px;
          font-weight: 900;
          color: #2b6777;
          margin-bottom: 8px;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          font-size: 16px;
          color: rgba(43,103,119,0.6);
          font-weight: 500;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
          animation: slideIn 0.6s ease-out;
        }

        .stat-card {
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(30px);
          border-radius: 20px;
          padding: 24px;
          border: 2px solid rgba(82,171,152,0.2);
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          transition: all 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(43,103,119,0.15);
        }

        .stat-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .stat-label {
          font-size: 14px;
          font-weight: 700;
          color: rgba(43,103,119,0.7);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: white;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .stat-description {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
        }

        .filter-section {
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(30px);
          border-radius: 20px;
          padding: 24px 28px;
          margin-bottom: 28px;
          border: 2px solid rgba(82,171,152,0.2);
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .search-box {
          position: relative;
          flex: 1;
          min-width: 280px;
        }

        .search-input {
          width: 100%;
          padding: 14px 20px 14px 48px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-weight: 500;
          color: #2b6777;
          outline: none;
          transition: all 0.3s;
          background: rgba(82,171,152,0.05);
        }

        .search-input:focus {
          border-color: #52ab98;
          background: white;
          box-shadow: 0 4px 20px rgba(82,171,152,0.15);
        }

        .search-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 18px;
          color: rgba(43,103,119,0.5);
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .filter-label {
          font-size: 14px;
          font-weight: 700;
          color: rgba(43,103,119,0.7);
        }

        .filter-select {
          padding: 12px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
          outline: none;
          cursor: pointer;
          background: rgba(82,171,152,0.05);
          transition: all 0.3s;
          min-width: 140px;
        }

        .filter-select:hover {
          border-color: #52ab98;
          background: white;
        }

        .requests-grid {
          display: grid;
          gap: 20px;
          animation: slideIn 0.6s ease-out;
        }

        .request-card {
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(30px);
          border-radius: 24px;
          border: 2px solid rgba(82,171,152,0.2);
          padding: 28px;
          transition: all 0.3s;
          cursor: pointer;
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          position: relative;
          overflow: hidden;
        }

        .request-card::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: transparent;
          transition: all 0.3s;
        }

        .request-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.2);
          border-color: rgba(82,171,152,0.4);
        }

        .request-card:hover::before {
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
        }

        .request-header-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 20px;
          padding-bottom: 20px;
          border-bottom: 2px solid rgba(82,171,152,0.1);
        }

        .request-left {
          flex: 1;
        }

        .request-id {
          font-size: 12px;
          font-weight: 700;
          color: #52ab98;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .request-issue-title {
          font-size: 20px;
          font-weight: 900;
          color: #2b6777;
          margin-bottom: 12px;
          letter-spacing: -0.3px;
        }

        .request-student-info {
          display: flex;
          align-items: center;
          gap: 14px;
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .request-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
        }

        .priority-badge {
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 800;
          text-transform: uppercase;
          color: white;
          letter-spacing: 0.5px;
        }

        .category-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          background: rgba(82,171,152,0.1);
          color: #2b6777;
          border: 1.5px solid rgba(82,171,152,0.2);
        }

        .request-description {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          line-height: 1.7;
          margin-bottom: 20px;
        }

        .request-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 14px;
        }

        .request-time {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
        }

        .status-badge {
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 800;
          text-transform: uppercase;
          color: white;
          letter-spacing: 0.5px;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        .modal {
          background: white;
          border-radius: 24px;
          max-width: 600px;
          width: 100%;
          max-height: 80vh;
          overflow: hidden;
          border: 2px solid rgba(82,171,152,0.2);
          box-shadow: 0 24px 70px rgba(43,103,119,0.3);
          animation: slideIn 0.4s ease;
        }

        .modal-header {
          padding: 28px 32px;
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(82,171,152,0.1);
          border: 1.5px solid rgba(82,171,152,0.25);
          color: #2b6777;
          cursor: pointer;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          font-size: 18px;
        }

        .modal-close:hover {
          background: rgba(82,171,152,0.2);
          transform: rotate(90deg);
        }

        .modal-content {
          padding: 28px 32px;
          max-height: calc(80vh - 180px);
          overflow-y: auto;
        }

        .modal-content::-webkit-scrollbar {
          width: 6px;
        }

        .modal-content::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .modal-content::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .modal-section {
          margin-bottom: 24px;
        }

        .modal-section-title {
          font-size: 12px;
          font-weight: 800;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 12px;
        }

        .modal-info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .modal-info-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 14px;
          background: rgba(82,171,152,0.05);
          border-radius: 12px;
          border: 1.5px solid rgba(82,171,152,0.1);
        }

        .modal-info-label {
          font-size: 12px;
          font-weight: 700;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .modal-info-value {
          font-size: 15px;
          font-weight: 700;
          color: #2b6777;
        }

        .modal-description {
          padding: 20px;
          background: rgba(82,171,152,0.05);
          border-radius: 14px;
          border: 1.5px solid rgba(82,171,152,0.1);
        }

        .modal-description-title {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 12px;
        }

        .modal-description-text {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          line-height: 1.7;
        }

        .modal-footer {
          padding: 24px 32px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .action-btn {
          flex: 1;
          min-width: 140px;
          padding: 14px 20px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-accept {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
        }

        .btn-accept:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(43,103,119,0.3);
        }

        .btn-reject {
          background: rgba(239,68,68,0.1);
          color: #dc2626;
          border: 2px solid rgba(239,68,68,0.2);
        }

        .btn-reject:hover:not(:disabled) {
          background: #dc2626;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(239,68,68,0.3);
        }

        .btn-complete {
          background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
          color: white;
        }

        .btn-complete:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(22,163,74,0.3);
        }

        .btn-reopen {
          background: rgba(59,130,246,0.1);
          color: #3b82f6;
          border: 2px solid rgba(59,130,246,0.2);
        }

        .btn-reopen:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(59,130,246,0.3);
        }

        .no-data {
          text-align: center;
          padding: 60px 24px;
          color: rgba(43,103,119,0.5);
          font-size: 16px;
          font-weight: 600;
        }

        @media (min-width: 1024px) {
          .sidebar {
            position: static;
            transform: translateX(0);
          }

          .close-btn {
            display: none;
          }

          .menu-toggle {
            display: none;
          }

          .overlay {
            display: none !important;
          }
        }

        @media (max-width: 1023px) {
          .close-btn {
            display: flex;
          }
        }

        @media (max-width: 768px) {
          .top-header {
            padding: 32px 16px;
          }

          .content-area {
            padding: 16px;
          }

          .page-header {
            padding: 32px 24px;
          }

          .page-title {
            font-size: 28px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .filter-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            min-width: 100%;
          }

          .request-header-row {
            flex-direction: column;
          }

          .request-right {
            flex-direction: row;
            width: 100%;
            align-items: center;
            justify-content: flex-start;
          }

          .modal-info-grid {
            grid-template-columns: 1fr;
          }

          .modal-footer {
            flex-direction: column;
          }

          .action-btn {
            width: 100%;
          }

          .notifications-panel {
            top: 90px;
            right: 16px;
            left: 16px;
            width: auto;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button onClick={() => setSidebarOpen(false)} className="close-btn">
                ✕
              </button>
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">{dashboardData?.adminName || 'Admin User'}</div>
                <div className="user-role-sidebar">{dashboardData?.hostelName || 'Hostel Administrator'}</div>
              </div>
            </div>
          </div>

          <div className="sidebar-nav">
            {/* Dynamically render menu sections based on user privileges */}
            
            {/* Overview Section */}
            {getPrivilegesByCategory('overview').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Overview</div>
                {getPrivilegesByCategory('overview').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    icon={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Management Section */}
            {getPrivilegesByCategory('management').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Management</div>
                {getPrivilegesByCategory('management').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    icon={privilege.page_icon} 
                    label={privilege.page_name} 
                    active={privilege.page_route === 'hostel-admin-request'}
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Approval Section */}
            {getPrivilegesByCategory('approval').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Approval</div>
                {getPrivilegesByCategory('approval').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    icon={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Settings Section */}
            {getPrivilegesByCategory('settings').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Settings</div>
                {getPrivilegesByCategory('settings').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    icon={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Reports Section */}
            {getPrivilegesByCategory('reports').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Reports</div>
                {getPrivilegesByCategory('reports').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    icon={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Show message if no privileges */}
            {userPrivileges.length === 0 && !privilegesLoading && (
              <div className="no-privileges-message">
                <div className="no-privileges-icon">🔒</div>
                <div className="no-privileges-text">No Access Granted</div>
                <div className="no-privileges-subtext">Please contact administrator</div>
              </div>
            )}
          </div>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>

        {/* Overlays */}
        <div 
          className={`overlay ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />
        <div
          className={`notification-overlay ${notificationOpen ? 'show' : ''}`}
          onClick={() => setNotificationOpen(false)}
        />

        {/* Main Content */}
        <div className="main-content">
          {/* Top Header */}
          <div className="top-header">
            <div className="header-left">
              <button onClick={() => setSidebarOpen(true)} className="menu-toggle">
                ☰
              </button>
              <div className="header-title-section">
                <div className="header-greeting">Request Management</div>
                <div className="header-name">{dashboardData?.hostelName || 'Admin Panel'}</div>
              </div>
            </div>
            <div className="header-right">

              <div className="user-avatar-header">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
          </div>

          {/* Notifications Panel */}
          <div className={`notifications-panel ${notificationOpen ? 'open' : ''}`}>
            <div className="notifications-header">
              <div className="notifications-title">Notifications</div>
              <button 
                className="close-notification-btn"
                onClick={() => setNotificationOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="notifications-list">
              {notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                >
                  <div className="notification-icon-wrapper">
                    {notification.icon}
                  </div>
                  <div className="notification-content">
                    <div className="notification-header-item">
                      <div className="notification-title">{notification.title}</div>
                      {!notification.read && <div className="notification-unread-dot"></div>}
                    </div>
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">{notification.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="content-area">
            {/* Page Header */}
            <div className="page-header">
              <h1 className="page-title">🔴 Live Maintenance Requests</h1>
              <p className="page-subtitle">Manage and track all student maintenance requests in real-time</p>
            </div>

            {/* Stats Grid */}
            {stats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-label">Total Requests</div>
                    <div className="stat-icon">📋</div>
                  </div>
                  <div className="stat-value">{stats.total || 0}</div>
                  <div className="stat-description">All time requests</div>
                </div>

                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-label">Pending</div>
                    <div className="stat-icon">⏳</div>
                  </div>
                  <div className="stat-value" style={{ color: '#f59e0b' }}>{stats.pending || 0}</div>
                  <div className="stat-description">Awaiting action</div>
                </div>

                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-label">In Progress</div>
                    <div className="stat-icon">🔄</div>
                  </div>
                  <div className="stat-value" style={{ color: '#3b82f6' }}>{stats.inProgress || 0}</div>
                  <div className="stat-description">Being resolved</div>
                </div>

                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-label">Completed</div>
                    <div className="stat-icon">✅</div>
                  </div>
                  <div className="stat-value" style={{ color: '#16a34a' }}>{stats.completed || 0}</div>
                  <div className="stat-description">Successfully resolved</div>
                </div>
              </div>
            )}

            {/* Filter Section */}
            <div className="filter-section">
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by student, room, or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <span className="filter-label">Category:</span>
                <select 
                  className="filter-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="electrical">Electrical</option>
                  <option value="plumbing">Plumbing</option>
                  <option value="ac">AC</option>
                  <option value="furniture">Furniture</option>
                  <option value="cleaning">Cleaning</option>
                  <option value="internet">Internet</option>
                  <option value="door">Door</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="filter-group">
                <span className="filter-label">Status:</span>
                <select 
                  className="filter-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="filter-group">
                <span className="filter-label">Priority:</span>
                <select 
                  className="filter-select"
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value)}
                >
                  <option value="all">All Priorities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            {/* Requests Grid */}
            {error ? (
              <div className="no-data">{error}</div>
            ) : requests.length > 0 ? (
              <div className="requests-grid">
                {requests.map((request) => (
                  <div 
                    key={request.request_id} 
                    className="request-card"
                    onClick={() => setSelectedRequest(request)}
                  >
                    <div className="request-header-row">
                      <div className="request-left">
                        <div className="request-id">{request.request_number}</div>
                        <h3 className="request-issue-title">{request.issue_type_label || 'Maintenance Issue'}</h3>
                        <div className="request-student-info">
                          <span>👤 {request.student_name}</span>
                          <span>•</span>
                          <span>🚪 Room {request.room_number}</span>
                        </div>
                      </div>
                      <div className="request-right">
                        <div 
                          className="priority-badge"
                          style={{ background: getPriorityColor(request.priority) }}
                        >
                          {request.priority}
                        </div>
                        <div className="category-badge">
                          <span>{getCategoryIcon(request.issue_type)}</span>
                          <span>{request.issue_type_label || request.issue_type}</span>
                        </div>
                      </div>
                    </div>
                    <p className="request-description">{request.description}</p>
                    <div className="request-footer">
                      <div className="request-time">
                        📅 {new Date(request.submitted_date).toLocaleDateString()} • 
                        🕐 {new Date(request.submitted_date).toLocaleTimeString()}
                      </div>
                      <div 
                        className="status-badge"
                        style={{ background: getStatusColor(request.status) }}
                      >
                        {request.status.replace('-', ' ')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                No maintenance requests found matching your filters
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Request Detail Modal */}
      {selectedRequest && (
        <div className="modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Request Details</h2>
              <button 
                className="modal-close"
                onClick={() => setSelectedRequest(null)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="modal-section">
                <div className="modal-section-title">Request Information</div>
                <div className="modal-info-grid">
                  <div className="modal-info-item">
                    <span className="modal-info-label">Request ID</span>
                    <span className="modal-info-value">{selectedRequest.request_number}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Category</span>
                    <span className="modal-info-value">
                      {getCategoryIcon(selectedRequest.issue_type)} {selectedRequest.issue_type_label || selectedRequest.issue_type}
                    </span>
                  </div>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Priority</span>
                    <span className="modal-info-value" style={{ color: getPriorityColor(selectedRequest.priority) }}>
                      {selectedRequest.priority.toUpperCase()}
                    </span>
                  </div>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Status</span>
                    <span className="modal-info-value" style={{ color: getStatusColor(selectedRequest.status) }}>
                      {selectedRequest.status.replace('-', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <div className="modal-section-title">Student Information</div>
                <div className="modal-info-grid">
                  <div className="modal-info-item">
                    <span className="modal-info-label">Student Name</span>
                    <span className="modal-info-value">{selectedRequest.student_name}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Phone Number</span>
                    <span className="modal-info-value">{selectedRequest.student_phone}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Room Number</span>
                    <span className="modal-info-value">Room {selectedRequest.room_number}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Submitted</span>
                    <span className="modal-info-value">
                      {new Date(selectedRequest.submitted_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {selectedRequest.assigned_to && (
                <div className="modal-section">
                  <div className="modal-section-title">Assignment</div>
                  <div className="modal-info-grid">
                    <div className="modal-info-item">
                      <span className="modal-info-label">Assigned To</span>
                      <span className="modal-info-value">{selectedRequest.assigned_to}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="modal-section">
                <div className="modal-section-title">Issue Description</div>
                <div className="modal-description">
                  <div className="modal-description-title">{selectedRequest.issue_type_label || selectedRequest.issue_type}</div>
                  <p className="modal-description-text">{selectedRequest.description}</p>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {selectedRequest.status === 'pending' && (
                <>
                  <button 
                    className="action-btn btn-accept"
                    onClick={() => updateRequestStatus(selectedRequest.request_id, 'in-progress', 'Maintenance Team')}
                    disabled={updateLoading}
                  >
                    ✓ {updateLoading ? 'Updating...' : 'Accept & Start'}
                  </button>
                  <button 
                    className="action-btn btn-reject"
                    onClick={() => updateRequestStatus(selectedRequest.request_id, 'cancelled')}
                    disabled={updateLoading}
                  >
                    ✕ {updateLoading ? 'Updating...' : 'Cancel'}
                  </button>
                </>
              )}
              {selectedRequest.status === 'in-progress' && (
                <button 
                  className="action-btn btn-complete"
                  onClick={() => updateRequestStatus(selectedRequest.request_id, 'completed')}
                  disabled={updateLoading}
                >
                  ✓ {updateLoading ? 'Updating...' : 'Mark as Completed'}
                </button>
              )}
              {(selectedRequest.status === 'completed' || selectedRequest.status === 'cancelled') && (
                <button 
                  className="action-btn btn-reopen"
                  onClick={() => updateRequestStatus(selectedRequest.request_id, 'pending')}
                  disabled={updateLoading}
                >
                  ↻ {updateLoading ? 'Updating...' : 'Reopen Request'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestManagementPage;